/**
 * Pure helpers for list screens. No React, no data fetching — the sort a
 * client-side table does, and the arithmetic a pager needs.
 */

export type SortDir = 'asc' | 'desc'

/**
 * Sort rows by one field. Strings compare with localeCompare (so Turkish
 * collation is right), everything else numerically.
 *
 * Lifted verbatim out of BusinessPage and InfrastructurePage, which had the
 * same comparator with a hand-written special case for their one string column.
 * Returns a new array — the caller's data is left alone.
 */
export function sortRows<T>(rows: readonly T[], key: keyof T, dir: SortDir): T[] {
  const sign = dir === 'asc' ? 1 : -1
  return [...rows].sort((a, b) => {
    const x = a[key]
    const y = b[key]
    if (typeof x === 'string' && typeof y === 'string') return sign * x.localeCompare(y)
    return sign * (Number(x) - Number(y))
  })
}

export interface PageRange {
  totalPages: number
  /** 1-based index of the first row on this page; 0 when there are none. */
  rangeStart: number
  rangeEnd: number
}

/** "Showing 21–40 of 340" — and how many pages that is. */
export function pageRange(page: number, pageSize: number, total: number): PageRange {
  return {
    totalPages: Math.max(1, Math.ceil(total / pageSize)),
    rangeStart: total === 0 ? 0 : (page - 1) * pageSize + 1,
    rangeEnd: Math.min(page * pageSize, total),
  }
}

/**
 * Page numbers to render, with gaps collapsed: [1, '…', 7, 8, 9, '…', 42].
 * Up to 7 pages are shown in full.
 */
export function pageNumbers(page: number, totalPages: number): Array<number | 'ellipsis'> {
  if (totalPages <= 7) return Array.from({ length: totalPages }, (_, i) => i + 1)

  const out: Array<number | 'ellipsis'> = [1]
  const start = Math.max(2, page - 1)
  const end = Math.min(totalPages - 1, page + 1)
  if (start > 2) out.push('ellipsis')
  for (let i = start; i <= end; i++) out.push(i)
  if (end < totalPages - 1) out.push('ellipsis')
  out.push(totalPages)
  return out
}
